import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { CheckCircle2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { Checklist, STEPS, stepState } from "@/components/app/Checklist";
import { BusinessForm, TimezoneForm } from "@/components/app/BusinessForms";
import { InvitePanel } from "@/components/app/InvitePanel";
import { MembersTable, ROLE_INFO } from "@/components/app/MembersTable";
import { PayrollForm } from "@/components/app/PayrollForm";
import { PlanPicker } from "@/components/app/PlanPicker";
import { NotificationPrefs } from "@/pages/app/Profile";
import { StepLocations, StepMap, StepGeofence, Primary } from "@/pages/app/onboarding/WorkplaceSteps";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StepAccount = ({ onNext }) => {
  const { user, refresh } = useAuth();
  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success(`Verification email sent to ${user.email}`); } catch (err) { toast.error(formatError(err)); }
  };
  if (user.email_verified) return (
    <div className="space-y-5"><div className="flex items-center gap-3 rounded-xl bg-emerald-50 p-4 text-emerald-800" data-testid="email-verified-status"><CheckCircle2 className="h-5 w-5" />{user.email} is verified.</div><Primary onClick={onNext} data-testid="step-continue-btn">Continue</Primary></div>
  );
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-amber-900" data-testid="email-unverified-status"><MailWarning className="h-5 w-5 shrink-0" /><p className="text-sm">We sent a verification link to <strong>{user.email}</strong>. Open it to continue setting up your business.</p></div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={resend} data-testid="resend-verification-btn">Resend email</Button>
        <Primary onClick={async () => { const d = await refresh(); if (!d?.user.email_verified) toast.error("Not verified yet — check your inbox"); }} data-testid="check-verified-btn">I've verified my email</Primary>
      </div>
    </div>
  );
};

const StepRoles = ({ onNext }) => (
  <div className="space-y-5">
    <div className="grid gap-3 sm:grid-cols-3">{Object.entries(ROLE_INFO).map(([r, d]) => <div key={r} className="rounded-xl border border-slate-200 p-4"><p className="font-semibold capitalize">{r}</p><p className="mt-1 text-xs text-slate-500">{d}</p></div>)}</div>
    <MembersTable />
    <Primary onClick={onNext} data-testid="step-continue-btn">Continue</Primary>
  </div>
);

const StepDone = ({ onNext }) => (
  <div className="rounded-2xl bg-[#0B2D5B] p-8 text-white">
    <p className="font-display text-2xl font-bold">You're all set.</p>
    <p className="mt-2 text-slate-300">Any skipped steps stay on your dashboard checklist so you can finish them later.</p>
    <Button onClick={onNext} className="mt-6 rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="go-to-dashboard-btn">Go to my dashboard</Button>
  </div>
);

const firstTodo = (business) => {
  if (!business) return 0;
  const i = STEPS.findIndex((s) => stepState(business, s.key) === "todo");
  return i === -1 ? 10 : i;
};

export default function Onboarding() {
  const { user, business: current, setBusiness, membership, refresh, switchBusiness, loadBusiness } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fresh = params.get("new") === "1";
  const business = fresh ? null : current;
  const [step, setStep] = useState(() => (fresh ? 1 : Math.max(0, STEPS.findIndex((s) => s.key === params.get("step")))));
  const init = useRef(!!params.get("step") || fresh);

  useEffect(() => {
    if (business && !init.current) { init.current = true; setStep(firstTodo(business)); }
  }, [business]);

  if (!fresh && membership && membership.role !== "owner") return <Navigate to="/app" replace />;
  const s = STEPS[step];
  const needsBiz = step >= 2;

  const mark = async (action) => {
    if (business && s.key !== "account") {
      try { const { data } = await api.post("/business/onboarding", { step: s.key, action }); setBusiness({ ...data, my_role: "owner" }); } catch (err) { toast.error(formatError(err)); return; }
    }
    if (s.key === "dashboard") { navigate("/app"); return; }
    setStep((x) => Math.min(x + 1, STEPS.length - 1));
  };
  const next = () => mark("complete");
  const onBizSaved = async (data) => {
    if (!business) { switchBusiness(data.business_id); await refresh(); if (fresh) navigate("/app/onboarding?step=timezone", { replace: true }); setStep(2); return; }
    await loadBusiness();
    next();
  };
  const reloadAndNext = async () => { await loadBusiness(); next(); };

  const body = () => {
    if (needsBiz && !business) return <p className="text-sm text-slate-500">Complete your business details first.</p>;
    switch (s.key) {
      case "account": return <StepAccount onNext={() => setStep(1)} />;
      case "business": return user.email_verified ? <BusinessForm business={business} onSaved={onBizSaved} submitLabel="Save & continue" /> : <p className="text-sm text-amber-700">Verify your email first (step 1).</p>;
      case "timezone": return <TimezoneForm business={business} onSaved={next} submitLabel="Save & continue" />;
      case "locations": return <StepLocations policy={business.settings.clock_policy} onNext={next} />;
      case "map": return <StepMap onNext={next} />;
      case "geofence": return <StepGeofence onNext={next} />;
      case "invite": return <div className="space-y-5"><InvitePanel /><Primary onClick={next} data-testid="step-continue-btn">Continue</Primary></div>;
      case "roles": return <StepRoles onNext={next} />;
      case "payroll": return <PayrollForm business={business} onSaved={reloadAndNext} submitLabel="Save & continue" />;
      case "notifications": return <div className="space-y-5"><p className="text-sm text-slate-600">Get alerts for missed clock-ins, people running late, leave requests and staff waiting for approval. You can change these any time in your profile.</p><NotificationPrefs /><Primary onClick={next} data-testid="step-continue-btn">Continue</Primary></div>;
      case "plan": return <PlanPicker business={business} onSaved={reloadAndNext} />;
      default: return <StepDone onNext={next} />;
    }
  };

  return (
    <div className="dot-bg min-h-screen p-4 sm:p-8" data-testid="onboarding-page">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-12">
        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-4">
          <Logo size="sm" to={business ? "/app" : "/"} />
          <div className="mt-6"><Checklist business={business || { onboarding: { completed: user.email_verified ? ["account"] : [], skipped: [] } }} active={step} onSelect={(i) => (i < 2 || business) && setStep(i)} /></div>
        </aside>
        <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 lg:col-span-8" data-testid="onboarding-step-panel">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#E07800]" data-testid="onboarding-step-label">Step {step + 1} of {STEPS.length}{s.optional ? " · optional" : ""}</p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl" data-testid="onboarding-step-title">{s.title}</h1>
          <div className="mt-8">{body()}</div>
          <div className="mt-10 flex items-center justify-between border-t border-slate-100 pt-6">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn">Back</Button>
            {s.optional && business && <Button variant="outline" className="rounded-full" onClick={() => mark("skip")} data-testid="onboarding-skip-btn">Skip for now</Button>}
          </div>
        </section>
      </div>
    </div>
  );
}
